import { stringify } from "query-string";
// import { fetchJson } from 'react-admin/lib/util/fetch';
import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  DELETE,
  fetchUtils,
} from "react-admin";
import isEqual from "lodash/isEqual";
import { clearAccessTokenFromCookies } from "./cookies";
const { fetchJson } = fetchUtils;

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
const createRestClient = (apiUrl, httpClient = fetchJson) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertRESTRequestToHTTP = (type, resource, params) => {
    let url = "";
    const options = {
      credentials: "include",
    };
    switch (type) {
      case GET_LIST: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort || {};
        const sort = field !== "id" ? field : "";
        const sortDir = field !== "id" ? order : "";

        const filterQuery = params.filter || {};
        const query = {
          offset: perPage ? page * perPage - perPage : undefined,
          limit: perPage,
          page: page - 1,
          sort,
          sortDir,
          ...filterQuery,
          // sort: JSON.stringify([field, order]),
          // range: JSON.stringify([
          //   (page - 1) * perPage,
          //   page * perPage - 1,
          // ]),
          // filter: JSON.stringify(params.filter),
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case GET_ONE:
        url = `${apiUrl}/${resource}/${params.id}`;
        break;
      case GET_MANY: {
        const query = {
          ids: params.ids.join(","),
          offset: 0,
          limit: 1000,
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          sort: JSON.stringify([field, order]),
          range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
          filter: JSON.stringify({
            ...params.filter,
            [params.target]: params.id,
          }),
        };
        url = `${apiUrl}/${resource}?${stringify(query)}`;
        break;
      }
      case UPDATE:
        const { data, previousData } = params;
        const dataToSend = {};
        if (data && previousData) {
          for (const key of Object.keys(data)) {
            if (!isEqual(data[key], previousData[key]) && data[key]) {
              dataToSend[key] = data[key];
            }
          }
        }

        url = data.password
          ? `${apiUrl}/admin/users/set-password/${params.id}`
          : `${apiUrl}/${resource}/${params.id}`;
        options.method = "PUT";
        options.body = JSON.stringify(dataToSend);
        break;
      case CREATE:
        url = `${apiUrl}/${resource}`;
        options.method = "POST";
        options.body = JSON.stringify(params.data);

        break;
      case DELETE:
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = "DELETE";
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The REST request params, depending on the type
   * @returns {Object} REST response
   */
  const convertHTTPResponseToREST = (response, type, resource, params) => {
    const { json } = response;
    const normalizeOne = (entity, i) => {
      if (!entity._id) {
        entity._id = i;
      }
      return { ...entity, id: entity._id };
    };
    const normalizeList = (list) => list.map(normalizeOne);

    switch (type) {
      case GET_LIST:
        return {
          data: normalizeList(json.data),
          total: json.total || json.data.length,
          /* we use || because response for /representatives-statistics Response don't contains key 'total' */
        };
      case GET_MANY:
        return {
          data: normalizeList(json.data),
          total: json.total,
        };
      case GET_ONE:
      case UPDATE:
        return { data: normalizeOne(json.data) };
      case CREATE:
        return { data: { ...params.data, id: json._id } };
      default:
        return { data: json };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a REST response
   */
  return (type, resource, params = {}) => {
    const { url, options } = convertRESTRequestToHTTP(type, resource, params);
    return httpClient(url, options)
      .then((response) =>
        convertHTTPResponseToREST(response, type, resource, params),
      )
      .catch((err) => {
        if (err.status === 401) {
          clearAccessTokenFromCookies();
          window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/login`;
        }
      });
  };
};

export default createRestClient(`${process.env.REACT_APP_BACKEND_URL}/api/v1`);



// WEBPACK FOOTER //
// ./src/trucknetRestClient.js